import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  getVersion,
  updateVersion,
  generateVersionLocalization,
  createMetadataUploadJob,
  createMetadataTranslationJob,
  createPageSiteDeployJob,
  triggerJob,
  getJob,
  getActiveTranslationJob,
  getMetadataTranslationFailedLocales,
  validateIOSMetadataUploadReadiness,
  validateAndroidMetadataUploadReadiness,
  validateMetadataUploadReadiness,
  PLATFORM_IOS,
  PLATFORM_ANDROID,
  type Application,
  type AppVersion,
  type Job,
  type PlatformLocalization,
} from '../api/client';
import { useApplication } from '../context/ApplicationContext';
import { AlertTriangle, ChevronDown, Copy, Languages, Loader2, Play, Save, Sparkles, Trash2, Upload } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import { useIsPro } from '../hooks/useIsPro';
import ProTag from './ProTag';
import AppPageLayout from './AppPageLayout';
import ModalLayout from './ModalLayout';
import PlatformSelectCards from './PlatformSelectCards';
import PageToolbar from './PageToolbar';
import LocaleSelectMenu from './LocaleSelectMenu';
import { TargetLocaleChecklist } from './SourceTargetLocalePicker';
import OnboardingTour, { type OnboardingStep } from './onboarding/OnboardingTour';
import {
  APP_STORE_LOCALIZATIONS,
  DEFAULT_APP_STORE_LOCALE,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';
const emptyPlatformLoc = (): PlatformLocalization => ({
  nameLocalized: {},
  subtitleLocalized: {},
  promotionalTextLocalized: {},
  descriptionLocalized: {},
  keywordsLocalized: {},
  releaseNotesLocalized: {},
});

const DEFAULT_PAGE_TYPES = ['support', 'privacy', 'privacy_choices'] as const;

function localizationHasContent(loc: PlatformLocalization, locale: string): boolean {
  return (
    ((loc.nameLocalized ?? {})[locale] ?? '').trim() !== '' ||
    ((loc.subtitleLocalized ?? {})[locale] ?? '').trim() !== '' ||
    ((loc.promotionalTextLocalized ?? {})[locale] ?? '').trim() !== '' ||
    ((loc.descriptionLocalized ?? {})[locale] ?? '').trim() !== '' ||
    ((loc.releaseNotesLocalized ?? {})[locale] ?? '').trim() !== '' ||
    ((loc.keywordsLocalized ?? {})[locale] ?? []).length > 0
  );
}

/** iOS metadata export: satırda veri var ama (açıklama veya anahtar kelime) birincil dilden de tamamlanamıyorsa true. */
function iosLocaleRowIncompleteForExport(
  loc: PlatformLocalization,
  locale: string,
  primary: string
): boolean {
  const hasRow =
    (loc.nameLocalized?.[locale] ?? '').trim() !== '' ||
    (loc.subtitleLocalized?.[locale] ?? '').trim() !== '' ||
    (loc.promotionalTextLocalized?.[locale] ?? '').trim() !== '' ||
    (loc.descriptionLocalized?.[locale] ?? '').trim() !== '' ||
    (loc.releaseNotesLocalized?.[locale] ?? '').trim() !== '' ||
    (loc.keywordsLocalized?.[locale] ?? []).length > 0;
  if (!hasRow) return false;
  const pDesc = (loc.descriptionLocalized?.[primary] ?? '').trim();
  const pKws = loc.keywordsLocalized?.[primary] ?? [];
  const desc = (loc.descriptionLocalized?.[locale] ?? '').trim() || pDesc;
  const kws = (loc.keywordsLocalized?.[locale] ?? []).length > 0 ? loc.keywordsLocalized![locale]! : pKws;
  const kwOk = Array.isArray(kws) && kws.length > 0;
  return !desc.trim() || !kwOk;
}


type FormState = {
  version: string;
  supportUrl: string;
  marketingUrl: string;
  privacyUrl: string;
  privacyChoicesUrl: string;
  /** Platform bazlı çeviriler; form alanları seçilen platforma göre buradan okunur/yazılır. */
  localizations: Record<string, PlatformLocalization>;
};

const emptyForm = (): FormState => ({
  version: '',
  supportUrl: '',
  marketingUrl: '',
  privacyUrl: '',
  privacyChoicesUrl: '',
  localizations: {
    [PLATFORM_IOS]: emptyPlatformLoc(),
    [PLATFORM_ANDROID]: emptyPlatformLoc(),
  },
});

/** API'den gelen version'ı form state'e çevirir. localizations varsa kullanır, yoksa düz alanlardan her iki platforma kopyalar. */
function versionToForm(v: AppVersion): FormState {
  const flat = {
    nameLocalized: { ...(v.nameLocalized ?? {}) },
    subtitleLocalized: { ...(v.subtitleLocalized ?? {}) },
    promotionalTextLocalized: { ...(v.promotionalTextLocalized ?? {}) },
    descriptionLocalized: { ...(v.descriptionLocalized ?? {}) },
    releaseNotesLocalized: {} as Record<string, string>,
    keywordsLocalized: v.keywordsLocalized
      ? Object.fromEntries(Object.entries(v.keywordsLocalized).map(([k, arr]) => [k, [...(arr ?? [])]]))
      : {},
  };
  if (v.localizations && Object.keys(v.localizations).length > 0) {
    const locs: Record<string, PlatformLocalization> = {};
    for (const platform of [PLATFORM_IOS, PLATFORM_ANDROID]) {
      const p = v.localizations[platform];
      locs[platform] = p
        ? {
            nameLocalized: { ...(p.nameLocalized ?? {}) },
            subtitleLocalized: { ...(p.subtitleLocalized ?? {}) },
            promotionalTextLocalized: { ...(p.promotionalTextLocalized ?? {}) },
            descriptionLocalized: { ...(p.descriptionLocalized ?? {}) },
            releaseNotesLocalized: { ...(p.releaseNotesLocalized ?? {}) },
            keywordsLocalized: p.keywordsLocalized
              ? Object.fromEntries(Object.entries(p.keywordsLocalized).map(([k, arr]) => [k, [...(arr ?? [])]]))
              : {},
          }
        : emptyPlatformLoc();
    }
    return {
      version: v.version ?? '',
      supportUrl: v.supportUrl ?? '',
      marketingUrl: v.marketingUrl ?? '',
      privacyUrl: v.privacyUrl ?? '',
      privacyChoicesUrl: v.privacyChoicesUrl ?? '',
      localizations: locs,
    };
  }
  return {
    version: v.version ?? '',
    supportUrl: v.supportUrl ?? '',
    marketingUrl: v.marketingUrl ?? '',
    privacyUrl: v.privacyUrl ?? '',
    privacyChoicesUrl: v.privacyChoicesUrl ?? '',
    localizations: {
      [PLATFORM_IOS]: { ...flat },
      [PLATFORM_ANDROID]: { ...flat },
    },
  };
}

const VersionEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { appId, versionId, tab } = useParams<{ appId: string; versionId: string; tab?: string }>();
  const [app, setApp] = useState<Application | null>(null);
  const [version, setVersion] = useState<AppVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [selectedLocale, setSelectedLocale] = useState(DEFAULT_APP_STORE_LOCALE);
  const [submitting, setSubmitting] = useState(false);
  const [generatingLoc, setGeneratingLoc] = useState(false); // AI Çeviri job oluşturma + tetikleme
  const [retryingFailedMetadataTranslation, setRetryingFailedMetadataTranslation] = useState(false);
  const [startingPendingTranslation, setStartingPendingTranslation] = useState(false);
  const [exportingMetadata, setExportingMetadata] = useState(false);
  const [generatingSuggestion, setGeneratingSuggestion] = useState(false); // Öneri oluştur (tek dil, summary'den)
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [selectedTargetLocalesForJob, setSelectedTargetLocalesForJob] = useState<string[]>([]);
  const [selectedPlatformsForTranslation, setSelectedPlatformsForTranslation] = useState<string[]>([PLATFORM_IOS]);
  const [translationLocaleSearch, setTranslationLocaleSearch] = useState('');
  const [collapsedTranslationLocaleGroups, setCollapsedTranslationLocaleGroups] = useState<
    Record<'filled' | 'empty', boolean>
  >({ filled: false, empty: false });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelectedPlatforms, setExportSelectedPlatforms] = useState<string[]>([PLATFORM_IOS, PLATFORM_ANDROID]);
  const [showClearLocalesModal, setShowClearLocalesModal] = useState(false);
  const [clearLocalesSelected, setClearLocalesSelected] = useState<string[]>([]);
  const [clearLocalesSaving, setClearLocalesSaving] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>(PLATFORM_IOS);
  const [activeTranslationJob, setActiveTranslationJob] = useState<Job | null>(null);
  const [activePageDeployJob, setActivePageDeployJob] = useState<Job | null>(null);
  const [pageDeploying, setPageDeploying] = useState(false);
  const { showError: showErrorPopup } = useError();
  const { isPro, goToUpgrade } = useIsPro();

  const {
    app: contextApp,
    loading: contextAppLoading,
    versions,
    refetch: refetchApplication,
  } = useApplication();

  const pagesEnabled = Boolean(contextApp?.pageHosting?.enabled);
  const isFirstVersion = useMemo(() => {
    if (!versionId || versions.length === 0) return false;
    let oldest = versions[0];
    for (const v of versions) {
      if (new Date(v.createdAt).getTime() < new Date(oldest.createdAt).getTime()) {
        oldest = v;
      }
    }
    return oldest.id === versionId;
  }, [versionId, versions]);

  const toggleExportPlatform = (p: string) => {
    setExportSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };
  const toggleTranslationPlatform = (p: string) => {
    setSelectedPlatformsForTranslation(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };
  const navigate = useNavigate();

  /** Pricing/availability uygulama seviyesine taşındı; eski URL'leri yönlendir. */
  useEffect(() => {
    if (!appId || !versionId) return;
    if (tab === 'pricing') {
      navigate(panelPath(`/applications/${appId}/pricing`), { replace: true });
      return;
    }
    if (tab === 'availability') {
      navigate(panelPath(`/applications/${appId}/availability`), { replace: true });
      return;
    }
    if (!tab || tab !== 'information') {
      navigate(panelPath(`/applications/${appId}/versions/${versionId}/information`), { replace: true });
    }
  }, [appId, versionId, tab, navigate]);

  /** Seçilen platformtaki lokalizasyon; form alanları buna göre doldurulur. */
  const currentPlatformLoc = useMemo(
    () => form.localizations[selectedPlatform] ?? emptyPlatformLoc(),
    [form.localizations, selectedPlatform]
  );


  const { allLocales, filledLocales } = useMemo(() => {
    const loc = currentPlatformLoc;
    const fromForm = new Set<string>([
      ...Object.keys(loc.nameLocalized ?? {}),
      ...Object.keys(loc.subtitleLocalized ?? {}),
      ...Object.keys(loc.promotionalTextLocalized ?? {}),
      ...Object.keys(loc.descriptionLocalized ?? {}),
      ...Object.keys(loc.releaseNotesLocalized ?? {}),
      ...Object.keys(loc.keywordsLocalized ?? {}),
    ]);
    const list = APP_STORE_LOCALIZATIONS.map(l => l.value);
    const extra = Array.from(fromForm).filter(v => !list.includes(v)).sort();
    const fullList = [...list, ...extra];
    const filled = fullList.filter(locale => localizationHasContent(loc, locale));
    const empty = fullList.filter(locale => !localizationHasContent(loc, locale));
    return {
      allLocales: [...filled, ...empty],
      filledLocales: filled,
    };
  }, [currentPlatformLoc]);

  const iosLocalizationForExport = form.localizations[PLATFORM_IOS] ?? emptyPlatformLoc();
  const primaryForIOSExport =
    (contextApp ?? app)?.primaryLanguage?.trim() || DEFAULT_APP_STORE_LOCALE;

  const iosFilledLocales = useMemo(() => {
    const loc = iosLocalizationForExport;
    const fromForm = new Set<string>([
      ...Object.keys(loc.nameLocalized ?? {}),
      ...Object.keys(loc.subtitleLocalized ?? {}),
      ...Object.keys(loc.promotionalTextLocalized ?? {}),
      ...Object.keys(loc.descriptionLocalized ?? {}),
      ...Object.keys(loc.releaseNotesLocalized ?? {}),
      ...Object.keys(loc.keywordsLocalized ?? {}),
    ]);
    const list = APP_STORE_LOCALIZATIONS.map(l => l.value);
    const extra = Array.from(fromForm).filter(v => !list.includes(v)).sort();
    const fullList = [...list, ...extra];
    const hasContent = (locale: string) =>
      ((loc.nameLocalized ?? {})[locale] ?? '').trim() !== '' ||
      ((loc.subtitleLocalized ?? {})[locale] ?? '').trim() !== '' ||
      ((loc.promotionalTextLocalized ?? {})[locale] ?? '').trim() !== '' ||
      ((loc.descriptionLocalized ?? {})[locale] ?? '').trim() !== '' ||
      ((loc.releaseNotesLocalized ?? {})[locale] ?? '').trim() !== '' ||
      ((loc.keywordsLocalized ?? {})[locale] ?? []).length > 0;
    return fullList.filter(hasContent);
  }, [iosLocalizationForExport]);

  const iosAttentionLocales = useMemo(
    () =>
      iosFilledLocales.filter(l =>
        iosLocaleRowIncompleteForExport(iosLocalizationForExport, l, primaryForIOSExport)
      ),
    [iosFilledLocales, iosLocalizationForExport, primaryForIOSExport]
  );

  const primaryLang =
    (contextApp ?? app)?.primaryLanguage?.trim() || 'en-US';
  const primaryLangHasContent =
    ((currentPlatformLoc.nameLocalized ?? {})[primaryLang] ?? '').trim() !== '' ||
    ((currentPlatformLoc.descriptionLocalized ?? {})[primaryLang] ?? '').trim() !== '';

  /** Çeviri için hedef dil listesi (primary hariç). Modal'da kullanılır. */
  const targetLocaleOptions = useMemo(() => {
    return APP_STORE_LOCALIZATIONS.filter(l => l.value !== primaryLang);
  }, [primaryLang]);

  const { filledTargetLocales, emptyTargetLocales } = useMemo(() => {
    const platforms =
      selectedPlatformsForTranslation.length > 0
        ? selectedPlatformsForTranslation
        : [selectedPlatform];
    const hasContent = (locale: string) =>
      platforms.some(platform =>
        localizationHasContent(form.localizations[platform] ?? emptyPlatformLoc(), locale)
      );
    return {
      filledTargetLocales: targetLocaleOptions.filter(locale => hasContent(locale.value)),
      emptyTargetLocales: targetLocaleOptions.filter(locale => !hasContent(locale.value)),
    };
  }, [
    form.localizations,
    selectedPlatform,
    selectedPlatformsForTranslation,
    targetLocaleOptions,
  ]);

  const { filteredFilledTargetLocales, filteredEmptyTargetLocales } = useMemo(() => {
    const q = translationLocaleSearch.trim().toLowerCase();
    const matches = (locale: { value: string; label: string }) =>
      locale.value.toLowerCase().includes(q) || locale.label.toLowerCase().includes(q);
    return {
      filteredFilledTargetLocales: q ? filledTargetLocales.filter(matches) : filledTargetLocales,
      filteredEmptyTargetLocales: q ? emptyTargetLocales.filter(matches) : emptyTargetLocales,
    };
  }, [emptyTargetLocales, filledTargetLocales, translationLocaleSearch]);

  const filteredTargetLocales = useMemo(
    () => [...filteredFilledTargetLocales, ...filteredEmptyTargetLocales],
    [filteredEmptyTargetLocales, filteredFilledTargetLocales]
  );

  /** Çevirileri temizle modal'ı: sadece veri girişi yapılmış diller listelenir. */
  const clearLocalesOptions = useMemo(
    () => filledLocales.map(value => ({ value, label: APP_STORE_LOCALIZATIONS.find(c => c.value === value)?.label ?? value })),
    [filledLocales]
  );

  const selectAllTargetLocales = () => {
    setSelectedTargetLocalesForJob(filteredTargetLocales.map(l => l.value));
  };
  const selectEmptyTargetLocales = () => {
    setSelectedTargetLocalesForJob(emptyTargetLocales.map(l => l.value));
  };
  const clearAllTargetLocales = () => {
    setSelectedTargetLocalesForJob([]);
  };
  const toggleTranslationLocaleGroup = (group: 'filled' | 'empty') => {
    setCollapsedTranslationLocaleGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleOpenClearLocalesModal = () => {
    setShowClearLocalesModal(true);
    setClearLocalesSelected([...filledLocales]);
  };


  /** Aktif dildeki (selectedLocale) lokalizasyonu bir platformdan diğerine kopyalar. */
  const copyLocaleToPlatform = useCallback(
    (sourcePlatform: string, targetPlatform: string) => {
      const loc = allLocales.includes(selectedLocale) ? selectedLocale : allLocales[0] ?? DEFAULT_APP_STORE_LOCALE;
      const src = form.localizations[sourcePlatform] ?? emptyPlatformLoc();
      const tgt = form.localizations[targetPlatform] ?? emptyPlatformLoc();
      setForm(f => ({
        ...f,
        localizations: {
          ...f.localizations,
          [targetPlatform]: {
            ...tgt,
            nameLocalized: { ...tgt.nameLocalized, [loc]: src.nameLocalized?.[loc] ?? '' },
            subtitleLocalized: { ...tgt.subtitleLocalized, [loc]: src.subtitleLocalized?.[loc] ?? '' },
            promotionalTextLocalized: { ...tgt.promotionalTextLocalized, [loc]: src.promotionalTextLocalized?.[loc] ?? '' },
            descriptionLocalized: { ...tgt.descriptionLocalized, [loc]: src.descriptionLocalized?.[loc] ?? '' },
            releaseNotesLocalized: isFirstVersion
              ? { ...(tgt.releaseNotesLocalized ?? {}) }
              : { ...tgt.releaseNotesLocalized, [loc]: src.releaseNotesLocalized?.[loc] ?? '' },
            keywordsLocalized: {
              ...tgt.keywordsLocalized,
              [loc]: Array.isArray(src.keywordsLocalized?.[loc]) ? [...src.keywordsLocalized[loc]] : [],
            },
          },
        },
      }));
    },
    [form.localizations, selectedLocale, allLocales, isFirstVersion]
  );

  const handleConfirmClearLocales = async () => {
    if (clearLocalesSelected.length === 0 || !appId || !versionId) return;
    const toRemove = new Set(clearLocalesSelected);
    const omitKeys = <T extends Record<string, unknown>>(obj: T): T => {
      const next = { ...obj };
      toRemove.forEach(k => {
        delete next[k];
      });
      return next as T;
    };
    const updatedLocalizations = Object.fromEntries(
      Object.entries(form.localizations).map(([platform, loc]) => [
        platform,
        {
          nameLocalized: omitKeys((loc?.nameLocalized ?? {}) as Record<string, unknown>) as Record<string, string>,
          subtitleLocalized: omitKeys((loc?.subtitleLocalized ?? {}) as Record<string, unknown>) as Record<string, string>,
          promotionalTextLocalized: omitKeys((loc?.promotionalTextLocalized ?? {}) as Record<string, unknown>) as Record<string, string>,
          descriptionLocalized: omitKeys((loc?.descriptionLocalized ?? {}) as Record<string, unknown>) as Record<string, string>,
          releaseNotesLocalized: omitKeys((loc?.releaseNotesLocalized ?? {}) as Record<string, unknown>) as Record<string, string>,
          keywordsLocalized: omitKeys((loc?.keywordsLocalized ?? {}) as Record<string, unknown>) as Record<string, string[]>,
        },
      ])
    );
    const updatedForm: FormState = {
      ...form,
      localizations: updatedLocalizations,
    };
    setForm(updatedForm);
    setClearLocalesSaving(true);
    try {
      await updateVersion(appId, versionId, {
        supportUrl: form.supportUrl,
        marketingUrl: form.marketingUrl,
        privacyUrl: form.privacyUrl,
        privacyChoicesUrl: form.privacyChoicesUrl,
        localizations: updatedLocalizations,
      });
      setShowClearLocalesModal(false);
      setClearLocalesSelected([]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('version.clearLocalesSaveFailed');
      showErrorPopup(msg);
    } finally {
      setClearLocalesSaving(false);
    }
  };


  const iosUploadReadinessIssues = useMemo(
    () => validateIOSMetadataUploadReadiness(contextApp ?? app, version),
    [contextApp, app, version]
  );

  const androidUploadReadinessIssues = useMemo(
    () => validateAndroidMetadataUploadReadiness(contextApp ?? app, version),
    [contextApp, app, version]
  );

  const exportUploadReadinessIssues = useMemo(
    () => validateMetadataUploadReadiness(contextApp ?? app, version, exportSelectedPlatforms),
    [contextApp, app, version, exportSelectedPlatforms]
  );

  const load = useCallback(async () => {
    if (!appId || !versionId) return;
    setLoading(true);
    setError(null);
    try {
      const versionData = await getVersion(appId, versionId);
      setVersion(versionData);
      setForm(versionToForm(versionData));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [appId, versionId, showErrorPopup]);

  useEffect(() => {
    if (appId && contextApp?.id === appId) {
      setApp(contextApp);
    } else if (!appId) {
      setApp(null);
    }
  }, [appId, contextApp]);

  useEffect(() => {
    let cancelled = false;
    if (!appId || !versionId) return;
    setLoading(true);
    setError(null);
    getVersion(appId, versionId)
      .then(versionData => {
        if (cancelled) return;
        setVersion(versionData);
        setForm(versionToForm(versionData));
      })
      .catch(e => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : t('common.loadFailed');
        setError(msg);
        showErrorPopup(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appId, versionId, showErrorPopup]);

  const primaryLanguage = (contextApp ?? app)?.primaryLanguage;
  // Primary dil app yüklendikten sonra gelir; version fetch'ini tekrar tetiklemesin.
  useEffect(() => {
    const primary = (primaryLanguage || '').trim();
    if (primary) setSelectedLocale(primary);
  }, [primaryLanguage]);

  // Sayfa açıldığında bu version için devam eden AI çeviri job'ı var mı kontrol et
  useEffect(() => {
    if (!appId || !versionId) return;
    getActiveTranslationJob(appId, versionId)
      .then(job => setActiveTranslationJob(job ?? null))
      .catch(() => setActiveTranslationJob(null));
  }, [appId, versionId]);

  // Aktif çeviri job'ı pending/processing ise progress için poll et (sadece id/status değişince effect yeniden kurulur)
  const translationJobId = activeTranslationJob?.id;
  const translationJobStatus = activeTranslationJob?.status;
  useEffect(() => {
    if (!translationJobId || (translationJobStatus !== 'pending' && translationJobStatus !== 'processing')) return;
    const t = setInterval(() => {
      getJob(translationJobId)
        .then(updated => setActiveTranslationJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [translationJobId, translationJobStatus]);

  // Çeviri tamamlanınca version'ı yeniden yükle (What's New / metadata güncellensin)
  useEffect(() => {
    if (translationJobStatus === 'completed' && appId && versionId) {
      void load();
    }
  }, [translationJobStatus, appId, versionId, load]);

  const pageDeployJobId = activePageDeployJob?.id;
  const pageDeployJobStatus = activePageDeployJob?.status;
  const pageDeployInProgress =
    pageDeploying || pageDeployJobStatus === 'pending' || pageDeployJobStatus === 'processing';

  useEffect(() => {
    if (!pageDeployJobId || (pageDeployJobStatus !== 'pending' && pageDeployJobStatus !== 'processing')) return;
    const timer = setInterval(() => {
      getJob(pageDeployJobId)
        .then(updated => setActivePageDeployJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [pageDeployJobId, pageDeployJobStatus]);

  // Page deploy tamamlanınca version URL'leri ve app EULA alanları güncellensin
  useEffect(() => {
    if (pageDeployJobStatus === 'completed' && appId && versionId) {
      void load();
      void refetchApplication().catch(() => {});
    }
  }, [pageDeployJobStatus, appId, versionId, load, refetchApplication]);

  const handleSaveAppInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!appId || !versionId) return;
    setSubmitting(true);
    try {
      const localizations = isFirstVersion
        ? Object.fromEntries(
            Object.entries(form.localizations).map(([platform, loc]) => [
              platform,
              { ...loc, releaseNotesLocalized: {} },
            ])
          )
        : form.localizations;
      await updateVersion(appId, versionId, {
        supportUrl: form.supportUrl,
        marketingUrl: form.marketingUrl,
        privacyUrl: form.privacyUrl,
        privacyChoicesUrl: form.privacyChoicesUrl,
        localizations,
      });
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.saveFailed');
      showErrorPopup(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAIGenerate = async () => {
    const locale = allLocales.includes(selectedLocale) ? selectedLocale : allLocales[0] ?? DEFAULT_APP_STORE_LOCALE;
    if (!appId || !versionId || !locale) return;
    setGeneratingSuggestion(true);
    try {
      const out = await generateVersionLocalization(appId, versionId, locale, selectedPlatform);
      const plat = selectedPlatform;
      setForm(f => ({
        ...f,
        localizations: {
          ...f.localizations,
          [plat]: {
            ...f.localizations[plat],
            nameLocalized: { ...(f.localizations[plat]?.nameLocalized ?? {}), [locale]: out.name },
            subtitleLocalized: { ...(f.localizations[plat]?.subtitleLocalized ?? {}), [locale]: out.subtitle },
            promotionalTextLocalized: { ...(f.localizations[plat]?.promotionalTextLocalized ?? {}), [locale]: out.promotionalText },
            descriptionLocalized: { ...(f.localizations[plat]?.descriptionLocalized ?? {}), [locale]: out.description },
            keywordsLocalized: { ...(f.localizations[plat]?.keywordsLocalized ?? {}), [locale]: out.keywords ? out.keywords.split(',').map(k => k.trim()).filter(Boolean) : [] },
          },
        },
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('version.aiGenerationFailed');
      showErrorPopup(msg);
    } finally {
      setGeneratingSuggestion(false);
    }
  };

  const handleGenerateDeployPages = async () => {
    if (!appId || !versionId || pageDeployInProgress) return;
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (!pagesEnabled) {
      showErrorPopup(t('panel.pageHostingNotEnabled'));
      return;
    }
    setPageDeploying(true);
    try {
      const { jobId } = await createPageSiteDeployJob(appId, versionId, {
        pageTypes: [...DEFAULT_PAGE_TYPES],
      });
      await triggerJob(jobId);
      const job = await getJob(jobId);
      setActivePageDeployJob(job);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('panel.pageDeployFailed');
      showErrorPopup(msg);
    } finally {
      setPageDeploying(false);
    }
  };

  /** AI çeviri modal'ını açar. */
  const handleOpenTranslationModal = () => {
    setShowTranslationModal(true);
    setSelectedTargetLocalesForJob([]);
    setSelectedPlatformsForTranslation(prev => (prev.length ? prev : [PLATFORM_IOS]));
    setTranslationLocaleSearch('');
    setCollapsedTranslationLocaleGroups({ filled: false, empty: false });
  };

  const metadataTranslationFailedLocales = useMemo(() => {
    if (!activeTranslationJob || activeTranslationJob.type !== 'metadata_translation') return [];
    return getMetadataTranslationFailedLocales(activeTranslationJob);
  }, [activeTranslationJob]);

  const canRetryFailedMetadataTranslationLocales =
    isPro &&
    metadataTranslationFailedLocales.length > 0 &&
    activeTranslationJob != null &&
    activeTranslationJob.status !== 'pending' &&
    activeTranslationJob.status !== 'processing';

  /** Aynı metadata_translation job'ını tetikler; worker yalnızca subProgress'te failed olan dilleri yeniden çevirir. */
  const handleRetryFailedMetadataTranslationLocales = async () => {
    if (!appId || !versionId || !activeTranslationJob) return;
    if (getMetadataTranslationFailedLocales(activeTranslationJob).length === 0) return;
    setRetryingFailedMetadataTranslation(true);
    try {
      await triggerJob(activeTranslationJob.id, { mode: 'failed_only' });
      const job = await getJob(activeTranslationJob.id);
      setActiveTranslationJob(job);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('version.retryFailedLocalesFailed');
      showErrorPopup(msg);
    } finally {
      setRetryingFailedMetadataTranslation(false);
    }
  };

  /** Pending metadata_translation job'ını sayfadan tetikler (job detayına gitmeden). */
  const handleStartPendingTranslationJob = async () => {
    if (!activeTranslationJob || activeTranslationJob.status !== 'pending') return;
    setStartingPendingTranslation(true);
    try {
      await triggerJob(activeTranslationJob.id);
      const job = await getJob(activeTranslationJob.id);
      setActiveTranslationJob(job);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('panel.translationJobStartFailed');
      showErrorPopup(msg);
    } finally {
      setStartingPendingTranslation(false);
    }
  };

  /** Seçilen dillere ve platformlara göre metadata_translation job oluşturur. Sayfada tamamlanma % gösterilir. */
  const handleStartTranslationWithLocales = async () => {
    if (!appId || !versionId) return;
    if (selectedTargetLocalesForJob.length === 0) {
      showErrorPopup(t('version.selectAtLeastOneLocale'));
      return;
    }
    if (selectedPlatformsForTranslation.length === 0) {
      showErrorPopup(t('version.selectAtLeastOnePlatform'));
      return;
    }
    setGeneratingLoc(true);
    try {
      const { jobId } = await createMetadataTranslationJob(
        appId,
        versionId,
        selectedTargetLocalesForJob,
        selectedPlatformsForTranslation
      );
      await triggerJob(jobId);
      setShowTranslationModal(false);
      const job = await getJob(jobId);
      setActiveTranslationJob(job);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('version.translationJobCreateFailed');
      showErrorPopup(msg);
    } finally {
      setGeneratingLoc(false);
    }
  };

  /** Metadata upload modal'ı açar. */
  const handleOpenExportModal = () => {
    setShowExportModal(true);
    setExportSelectedPlatforms(prev => (prev.length ? prev : [PLATFORM_IOS, PLATFORM_ANDROID]));
  };

  /** Seçilen platformlara göre metadata_upload job'ı (export + store upload) oluşturur; job detayına yönlendirir. */
  const handleConfirmExportMetadata = async () => {
    if (!appId || !versionId) return;
    if (exportSelectedPlatforms.length === 0) {
      showErrorPopup(t('panel.metadataUploadSelectPlatform'));
      return;
    }
    if (exportSelectedPlatforms.includes(PLATFORM_IOS) && !form.supportUrl.trim()) {
      showErrorPopup(t('panel.metadataUploadSupportUrlRequired'));
      return;
    }
    setExportingMetadata(true);
    try {
      const { jobId } = await createMetadataUploadJob(appId, versionId, exportSelectedPlatforms);
      await triggerJob(jobId);
      setShowExportModal(false);
      navigate(panelPath(`/jobs/${jobId}`));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('version.metadataUploadFailed');
      showErrorPopup(msg);
    } finally {
      setExportingMetadata(false);
    }
  };

  if (!appId || !versionId) return null;

  const loc = selectedLocale || DEFAULT_APP_STORE_LOCALE;
  const nameVal = (currentPlatformLoc.nameLocalized && currentPlatformLoc.nameLocalized[loc]) ?? '';
  const subtitleVal = (currentPlatformLoc.subtitleLocalized && currentPlatformLoc.subtitleLocalized[loc]) ?? '';
  const subtitleMaxLen = selectedPlatform === PLATFORM_ANDROID ? 80 : 30;
  const promotionalVal = (currentPlatformLoc.promotionalTextLocalized && currentPlatformLoc.promotionalTextLocalized[loc]) ?? '';
  const releaseNotesVal = (currentPlatformLoc.releaseNotesLocalized && currentPlatformLoc.releaseNotesLocalized[loc]) ?? '';
  const descriptionVal = (currentPlatformLoc.descriptionLocalized && currentPlatformLoc.descriptionLocalized[loc]) ?? '';
  const keywordsVal = Array.isArray(currentPlatformLoc.keywordsLocalized?.[loc])
    ? currentPlatformLoc.keywordsLocalized[loc]
    : [];

  const versionTourSteps: OnboardingStep[] = [
    { target: '[data-tour="version-locale-select"]', title: t('onboarding.versionEdit.step1Title'), body: t('onboarding.versionEdit.step1Body') },
    { target: '[data-tour="version-metadata-form"]', title: t('onboarding.versionEdit.step2Title'), body: t('onboarding.versionEdit.step2Body') },
    { target: '[data-tour="version-publish"]', title: t('onboarding.versionEdit.step3Title'), body: t('onboarding.versionEdit.step3Body') },
  ];

  return (
    <AppPageLayout
      appId={appId}
      app={app}
      loading={loading || (Boolean(appId) && contextAppLoading)}
      error={error || (!version ? t('version.notFound') : null)}
      pageTitle={version ? `${t('version.appInformationLabel')} · v${version.version}` : undefined}
    >
      {app && version && (
        <>
        <OnboardingTour tourId="version-edit" steps={versionTourSteps} />
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 has-[[data-stuck]]:!rounded-t-none">
          {error && (
            <div className="mx-4 mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {activePageDeployJob && (
            <div
              className={
                activePageDeployJob.status === 'failed'
                  ? 'mx-4 mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : activePageDeployJob.status === 'completed'
                    ? 'mx-4 mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'mx-4 mt-4 p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800'
              }
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles className="h-4 w-4 text-sky-600 dark:text-sky-400 shrink-0" />
                  {activePageDeployJob.status === 'pending' || activePageDeployJob.status === 'processing' ? (
                    <>
                      <span className="text-sm font-medium text-sky-900 dark:text-sky-100">
                        {t('panel.pageDeployJobInProgress')}
                      </span>
                      <span className="text-sm font-semibold text-sky-700 dark:text-sky-300">
                        %{activePageDeployJob.progress ?? 0}
                      </span>
                    </>
                  ) : activePageDeployJob.status === 'completed' ? (
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      {t('panel.pageDeployJobCompleted')}
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('panel.pageDeployJobFailed')}
                      {activePageDeployJob.errorMessage ? `: ${activePageDeployJob.errorMessage}` : ''}
                    </span>
                  )}
                </div>
                <Link
                  to={panelPath(`/jobs/${activePageDeployJob.id}`)}
                  className="text-sm font-medium text-sky-800 dark:text-sky-200 hover:underline"
                >
                  {t('panel.jobDetailLink')}
                </Link>
              </div>
              {(activePageDeployJob.status === 'pending' || activePageDeployJob.status === 'processing') && (
                <div className="mt-2 h-1.5 w-full rounded-full bg-sky-200/70 dark:bg-sky-900/50 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-sky-500 transition-all duration-300"
                    style={{ width: `${activePageDeployJob.progress ?? 0}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {activeTranslationJob && (
            <div
              className={
                activeTranslationJob.status === 'failed'
                  ? 'mx-4 mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : activeTranslationJob.status === 'completed'
                    ? 'mx-4 mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'mx-4 mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              }
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  {activeTranslationJob.status === 'pending' ? (
                    <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      {t('panel.translationJobPending')}
                    </span>
                  ) : activeTranslationJob.status === 'processing' ? (
                    <>
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                        {t('panel.translationJobInProgress')}
                      </span>
                      <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                        %{activeTranslationJob.progress ?? 0}
                      </span>
                    </>
                  ) : activeTranslationJob.status === 'completed' ? (
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      {t('panel.translationJobCompleted')}
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('panel.translationJobFailed')}
                      {activeTranslationJob.errorMessage ? `: ${activeTranslationJob.errorMessage}` : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {activeTranslationJob.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => void handleStartPendingTranslationJob()}
                      disabled={startingPendingTranslation}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {startingPendingTranslation ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      {startingPendingTranslation ? t('common.saving') : t('panel.start')}
                    </button>
                  )}
                  <Link
                    to={panelPath(`/jobs/${activeTranslationJob.id}`)}
                    className="text-sm font-medium text-amber-800 dark:text-amber-200 hover:underline"
                  >
                    {t('panel.jobDetailLink')}
                  </Link>
                </div>
              </div>
              {activeTranslationJob.status === 'processing' && (
                <div className="mt-2 h-1.5 w-full rounded-full bg-amber-200/70 dark:bg-amber-900/50 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${activeTranslationJob.progress ?? 0}%` }}
                  />
                </div>
              )}
              {canRetryFailedMetadataTranslationLocales && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400 w-full sm:w-auto">
                    {t('version.retryFailedLocalesInfo', { count: metadataTranslationFailedLocales.length })}{' '}
                    <span className="font-mono text-gray-800 dark:text-gray-200">{metadataTranslationFailedLocales.join(', ')}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleRetryFailedMetadataTranslationLocales()}
                    disabled={retryingFailedMetadataTranslation}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {retryingFailedMetadataTranslation ? t('version.starting') : t('version.retryFailedLocales')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Sayfa üstü toolbar: context (sol) → AI → publish (sağ) */}
          <PageToolbar>
            <div className="inline-flex h-8 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-0.5">
              <button
                type="button"
                onClick={() => setSelectedPlatform(PLATFORM_IOS)}
                title={t('version.iosLocalizationTitle')}
                className={clsx(
                  'inline-flex items-center gap-1.5 h-full px-2.5 rounded-md text-sm font-medium transition-colors',
                  selectedPlatform === PLATFORM_IOS
                    ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                )}
              >
                <span className="text-base leading-none" aria-hidden>🍎</span>
                iOS
              </button>
              <button
                type="button"
                onClick={() => setSelectedPlatform(PLATFORM_ANDROID)}
                title={t('version.androidLocalizationTitle')}
                className={clsx(
                  'inline-flex items-center gap-1.5 h-full px-2.5 rounded-md text-sm font-medium transition-colors',
                  selectedPlatform === PLATFORM_ANDROID
                    ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                )}
              >
                <span className="text-base leading-none" aria-hidden>🤖</span>
                Android
              </button>
            </div>

            <div data-tour="version-locale-select">
            <LocaleSelectMenu
              id="version-edit-locale"
              value={allLocales.includes(selectedLocale) ? selectedLocale : allLocales[0] ?? DEFAULT_APP_STORE_LOCALE}
              onChange={setSelectedLocale}
              preferredLocales={filledLocales.length > 0 ? filledLocales : [primaryLang]}
              primaryLocale={primaryLang}
              renderOptionExtra={locale =>
                selectedPlatform === PLATFORM_IOS && iosAttentionLocales.includes(locale) ? (
                  <AlertTriangle
                    className="h-3.5 w-3.5 shrink-0 text-amber-500"
                    aria-label={t('version.missingDescriptionOrKeywords')}
                  />
                ) : null
              }
            />
            </div>

            <button
              type="button"
              onClick={() => {
                const target =
                  selectedPlatform === PLATFORM_IOS ? PLATFORM_ANDROID : PLATFORM_IOS;
                copyLocaleToPlatform(selectedPlatform, target);
              }}
              title={
                selectedPlatform === PLATFORM_IOS
                  ? t('version.copyIosToAndroid')
                  : t('version.copyAndroidToIos')
              }
              className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg border border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 text-sm font-medium transition-colors"
            >
              <Copy className="h-4 w-4 shrink-0" />
              <span className="hidden xl:inline">
                {selectedPlatform === PLATFORM_IOS ? t('version.iosToAndroidShort') : t('version.androidToIosShort')}
              </span>
            </button>

            <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
              <div className="flex items-center gap-1 rounded-xl p-1 bg-primary-50 dark:bg-primary-950/50 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70">
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isPro) {
                      goToUpgrade();
                      return;
                    }
                    handleOpenTranslationModal();
                  }}
                  disabled={generatingLoc || !primaryLangHasContent}
                  title={
                    !isPro
                      ? t('proFeature.requiresPro')
                      : !primaryLangHasContent
                        ? t('version.aiTranslateNeedsPrimaryContent')
                        : t('version.aiTranslateHint')
                  }
                  className={clsx(
                    'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                    isPro
                      ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 disabled:opacity-50 disabled:pointer-events-none'
                      : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                  )}
                >
                  {generatingLoc ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('version.jobLoading')}
                    </span>
                  ) : (
                    <>
                      <Languages className="h-4 w-4 shrink-0" />
                      {t('version.aiTranslate')}
                      {!isPro && <ProTag />}
                    </>
                  )}
                </button>
              </div>

              <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />

              <div data-tour="version-publish" className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isPro) {
                      goToUpgrade();
                      return;
                    }
                    handleOpenExportModal();
                  }}
                  disabled={exportingMetadata || filledLocales.length === 0}
                  title={
                    !isPro
                      ? t('proFeature.requiresPro')
                      : filledLocales.length === 0
                        ? t('panel.metadataUploadNeedLocales')
                        : t('panel.metadataUploadModalDescription')
                  }
                  className={clsx(
                    'inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg text-sm font-medium border transition-colors',
                    isPro
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:pointer-events-none'
                      : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-pointer'
                  )}
                >
                  {exportingMetadata ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('panel.metadataUploadRunning')}
                    </span>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 shrink-0" />
                      <span className="hidden lg:inline">{t('panel.metadataUpload')}</span>
                      {!isPro && <ProTag />}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOpenClearLocalesModal}
                  disabled={filledLocales.length === 0}
                  title={
                    filledLocales.length === 0
                      ? t('version.noDataToClear')
                      : t('version.clearLocalesHint')
                  }
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg border border-transparent text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                  <span className="hidden xl:inline">{t('common.clear')}</span>
                </button>
                <button
                  type="submit"
                  form="version-app-info-form"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  <Save className="h-4 w-4 shrink-0" />
                  {submitting ? t('panel.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </PageToolbar>

          {/* App Information başlık — seçilen platforma göre */}
          <div data-tour="version-metadata-form" className="p-4 pt-4 mb-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {t('version.appInformationLabel')}
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({selectedPlatform === PLATFORM_IOS ? t('version.appStoreIosLabel') : t('version.playStoreAndroidLabel')})
              </span>
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('version.appInfoDescription')}
            </p>
          </div>

          <form
              id="version-app-info-form"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSaveAppInfo(e);
              }}
              className="space-y-4 max-w-2xl px-4 pb-4"
              noValidate
            >
              {/* Information alanları */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isPro) {
                        goToUpgrade();
                        return;
                      }
                      handleAIGenerate();
                    }}
                    disabled={generatingSuggestion || !app?.summary?.trim()}
                    title={!isPro ? t('proFeature.requiresPro') : !app?.summary?.trim() ? t('version.suggestNeedsSummary') : t('version.suggestHint')}
                    className={clsx(
                      'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                      isPro
                        ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 disabled:opacity-50 disabled:pointer-events-none'
                        : 'bg-primary-50 dark:bg-primary-950/50 text-gray-500 dark:text-gray-400 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70 cursor-pointer'
                    )}
                  >
                    {generatingSuggestion ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        {t('version.suggesting')}
                      </span>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 shrink-0" />
                        {t('version.generateSuggestion')}
                        {!isPro && <ProTag />}
                      </>
                    )}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('version.nameLabel')} <span className="text-gray-400 font-normal">(max 30)</span></label>
                  <input
                    type="text"
                    maxLength={30}
                    value={nameVal}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            nameLocalized: { ...(f.localizations[selectedPlatform]?.nameLocalized ?? {}), [loc]: e.target.value },
                          },
                        },
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.appNamePlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{nameVal.length}/30</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {selectedPlatform === PLATFORM_ANDROID ? t('version.subtitleAndroidLabel') : t('version.subtitleLabel')}{' '}
                    <span className="text-gray-400 font-normal">(max {subtitleMaxLen})</span>
                  </label>
                  <input
                    type="text"
                    maxLength={subtitleMaxLen}
                    value={subtitleVal}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            subtitleLocalized: { ...(f.localizations[selectedPlatform]?.subtitleLocalized ?? {}), [loc]: e.target.value },
                          },
                        },
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.subtitlePlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{subtitleVal.length}/{subtitleMaxLen}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('version.promotionalTextLabel')} <span className="text-gray-400 font-normal">(max 170)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={170}
                    value={promotionalVal}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            promotionalTextLocalized: { ...(f.localizations[selectedPlatform]?.promotionalTextLocalized ?? {}), [loc]: e.target.value },
                          },
                        },
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.promotionalPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{promotionalVal.length}/170</p>
                </div>
                {!isFirstVersion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('version.whatsNewLabel')} <span className="text-gray-400 font-normal">(max 4000)</span>
                  </label>
                  <textarea
                    rows={3}
                    maxLength={4000}
                    value={releaseNotesVal}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            releaseNotesLocalized: { ...(f.localizations[selectedPlatform]?.releaseNotesLocalized ?? {}), [loc]: e.target.value },
                          },
                        },
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.releaseNotesPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{releaseNotesVal.length}/4000</p>
                </div>
                )}
                <div>
                  {selectedPlatform === PLATFORM_IOS &&
                    iosLocaleRowIncompleteForExport(iosLocalizationForExport, loc, primaryForIOSExport) && (
                      <div className="mb-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 text-sm flex gap-2">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                        <span>
                          {t('version.iosLocaleIncompleteWarning', {
                            primary: APP_STORE_LOCALIZATIONS.find(c => c.value === primaryForIOSExport)?.label ?? primaryForIOSExport,
                          })}
                        </span>
                      </div>
                    )}
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('version.descriptionLabel')} <span className="text-gray-400 font-normal">(max 4000)</span>
                  </label>
                  <textarea
                    rows={3}
                    maxLength={4000}
                    value={descriptionVal}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            descriptionLocalized: { ...(f.localizations[selectedPlatform]?.descriptionLocalized ?? {}), [loc]: e.target.value },
                          },
                        },
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.descriptionPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{descriptionVal.length}/4000</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('version.keywordsLabel')} <span className="text-gray-400 font-normal">{t('version.keywordsHint')}</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={keywordsVal.join(', ')}
                    onChange={e => {
                      const raw = e.target.value.slice(0, 100);
                      const arr = raw
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .slice(0, 15);
                      setForm(f => ({
                        ...f,
                        localizations: {
                          ...f.localizations,
                          [selectedPlatform]: {
                            ...(f.localizations[selectedPlatform] ?? emptyPlatformLoc()),
                            keywordsLocalized: { ...(f.localizations[selectedPlatform]?.keywordsLocalized ?? {}), [loc]: arr },
                          },
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.keywordsPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{keywordsVal.join(', ').length}/100</p>
                </div>
              </div>

              {/* Support & links — Information'dan sonra */}
              <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('version.supportAndLinks')}</h4>
                  <button
                    type="button"
                    disabled={pageDeployInProgress || !pagesEnabled}
                    onClick={() => void handleGenerateDeployPages()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!pagesEnabled ? t('panel.pageHostingNotEnabled') : undefined}
                  >
                    {pageDeployInProgress ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {t('panel.generateDeployPages')}
                    {!isPro ? <ProTag /> : null}
                  </button>
                </div>
                {!pagesEnabled && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('panel.pageHostingNotEnabledHint')}
                  </p>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('version.supportUrlLabel')}</label>
                  <input
                    type="url"
                    value={form.supportUrl}
                    onChange={e => setForm(f => ({ ...f, supportUrl: e.target.value }))}
                    className={clsx(
                      'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700',
                      !form.supportUrl.trim()
                        ? 'border-amber-400 dark:border-amber-600'
                        : 'border-gray-300 dark:border-gray-600'
                    )}
                    placeholder={t('version.urlPlaceholder')}
                  />
                  {!form.supportUrl.trim() && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                      {t('version.supportUrlRequiredWarning')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('version.marketingUrlLabel')}</label>
                  <input
                    type="url"
                    value={form.marketingUrl}
                    onChange={e => setForm(f => ({ ...f, marketingUrl: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.urlPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('version.privacyUrlLabel')}</label>
                  <input
                    type="url"
                    value={form.privacyUrl}
                    onChange={e => setForm(f => ({ ...f, privacyUrl: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.urlPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('panel.privacyChoicesUrl')}
                  </label>
                  <input
                    type="url"
                    value={form.privacyChoicesUrl}
                    onChange={e => setForm(f => ({ ...f, privacyChoicesUrl: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    placeholder={t('version.urlPlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('panel.privacyChoicesUrlHint')}
                  </p>
                </div>
              </div>
          </form>
        </div>

        {/* AI Çeviri: çevrilecek dilleri seçme modal'ı */}
        <ModalLayout
          open={showTranslationModal}
          onClose={() => setShowTranslationModal(false)}
          title={t('version.selectTargetLocalesTitle')}
          icon={<Languages className="h-5 w-5 text-primary-500" />}
          description={`${t('version.sourceLabel')} ${getLocaleFlag(primaryLang)} ${APP_STORE_LOCALIZATIONS.find(l => l.value === primaryLang)?.label ?? primaryLang}`}
          primaryLabel={generatingLoc ? t('version.starting') : t('version.startWithCount', { count: selectedTargetLocalesForJob.length })}
          onPrimary={handleStartTranslationWithLocales}
          primaryDisabled={generatingLoc || selectedTargetLocalesForJob.length === 0 || selectedPlatformsForTranslation.length === 0}
          primaryLoading={generatingLoc}
          closeDisabled={generatingLoc}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('version.platformLabel')}</p>
              <PlatformSelectCards
                options={[
                  { value: PLATFORM_IOS, label: t('panel.appStore') },
                  { value: PLATFORM_ANDROID, label: t('panel.playStore') },
                ]}
                selected={selectedPlatformsForTranslation}
                onToggle={toggleTranslationPlatform}
              />
            </div>
            <div className="space-y-3">
              <input
                type="text"
                value={translationLocaleSearch}
                onChange={e => setTranslationLocaleSearch(e.target.value)}
                placeholder={t('version.searchLocalePlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
              />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={selectAllTargetLocales} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  {t('panel.selectAll')}
                </button>
                <button type="button" onClick={selectEmptyTargetLocales} disabled={emptyTargetLocales.length === 0} className="px-3 py-1.5 rounded-lg text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 disabled:opacity-50">
                  {t('version.selectEmptyLocales')}
                </button>
                <button type="button" onClick={clearAllTargetLocales} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  {t('appScreen.clearAll')}
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {filteredTargetLocales.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">{t('version.noMatchingLocale')}</p>
              ) : (
                [
                  { key: 'filled' as const, title: t('version.localesFilledGroup'), locales: filteredFilledTargetLocales },
                  { key: 'empty' as const, title: t('version.localesEmptyGroup'), locales: filteredEmptyTargetLocales },
                ]
                  .filter(group => group.locales.length > 0)
                  .map(group => (
                    <div key={group.key}>
                      <button
                        type="button"
                        onClick={() => toggleTranslationLocaleGroup(group.key)}
                        aria-expanded={!collapsedTranslationLocaleGroups[group.key]}
                        className="mb-1.5 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50"
                      >
                        <span>{group.title} ({group.locales.length})</span>
                        <ChevronDown className={clsx('h-4 w-4 transition-transform', collapsedTranslationLocaleGroups[group.key] && '-rotate-90')} />
                      </button>
                      {!collapsedTranslationLocaleGroups[group.key] && (
                        <TargetLocaleChecklist
                          selected={selectedTargetLocalesForJob}
                          onChange={setSelectedTargetLocalesForJob}
                          locales={group.locales}
                          label=""
                          showSelectAll={false}
                          showClearAll={false}
                        />
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        </ModalLayout>

        {/* Metadata Upload: yalnızca bu version’ın listing metadata’sı */}
        <ModalLayout
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          closeDisabled={exportingMetadata}
          title={t('panel.metadataUploadModalTitle')}
          icon={<Upload className="h-5 w-5 text-primary-500" />}
          description={t('panel.metadataUploadModalDescription')}
          primaryLabel={
            exportingMetadata
              ? t('panel.metadataUploadRunning')
              : `${t('panel.metadataUpload')} (${exportSelectedPlatforms.length})`
          }
          onPrimary={handleConfirmExportMetadata}
          primaryDisabled={
            exportSelectedPlatforms.length === 0 ||
            (exportSelectedPlatforms.includes(PLATFORM_IOS) && !form.supportUrl.trim())
          }
          primaryLoading={exportingMetadata}
          maxWidth="md"
        >
          <PlatformSelectCards
            options={[
              { value: PLATFORM_IOS, label: t('panel.appStore') },
              { value: PLATFORM_ANDROID, label: t('panel.playStore') },
            ]}
            selected={exportSelectedPlatforms}
            onToggle={toggleExportPlatform}
          />
          {exportSelectedPlatforms.includes(PLATFORM_IOS) && !form.supportUrl.trim() && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
              {t('version.exportSupportUrlRequired')}
            </p>
          )}
          {exportSelectedPlatforms.includes(PLATFORM_IOS) && iosUploadReadinessIssues.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('panel.iosUploadReadinessTitle')}
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                {iosUploadReadinessIssues.includes('reviewContactUser') && (
                  <li><Link to={panelPath(`/applications/${appId}`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssueReviewContact')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('iosBundleId') && (
                  <li><Link to={panelPath(`/applications/${appId}`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssueBundleId')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('iosPricing') && (
                  <li><Link to={panelPath(`/applications/${appId}/pricing`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssuePricing')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('iosAvailability') && (
                  <li><Link to={panelPath(`/applications/${appId}/availability`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssueAvailability')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('iosAvailabilityTerritories') && (
                  <li><Link to={panelPath(`/applications/${appId}/availability`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssueAvailabilityTerritories')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('customEulaText') && (
                  <li><Link to={panelPath(`/applications/${appId}`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssueCustomEulaText')}</Link></li>
                )}
                {iosUploadReadinessIssues.includes('primaryCategory') && (
                  <li><Link to={panelPath(`/applications/${appId}`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.iosUploadIssuePrimaryCategory')}</Link></li>
                )}
              </ul>
            </div>
          )}
          {exportSelectedPlatforms.includes(PLATFORM_ANDROID) && androidUploadReadinessIssues.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('panel.androidUploadReadinessTitle')}
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                {androidUploadReadinessIssues.includes('androidBundleId') && (
                  <li><Link to={panelPath(`/applications/${appId}`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.androidUploadIssueBundleId')}</Link></li>
                )}
                {androidUploadReadinessIssues.includes('androidAvailability') && (
                  <li><Link to={panelPath(`/applications/${appId}/availability`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.androidUploadIssueAvailability')}</Link></li>
                )}
                {androidUploadReadinessIssues.includes('androidAvailabilityCountries') && (
                  <li><Link to={panelPath(`/applications/${appId}/availability`)} className="underline hover:text-amber-900 dark:hover:text-amber-100">{t('panel.androidUploadIssueAvailabilityCountries')}</Link></li>
                )}
              </ul>
            </div>
          )}
          {exportUploadReadinessIssues.length > 0 && exportSelectedPlatforms.length === 0 && (
            <div className="mt-3 text-sm text-gray-500">{t('panel.selectPlatformForUpload')}</div>
          )}
          {exportSelectedPlatforms.includes(PLATFORM_IOS) && iosAttentionLocales.length > 0 && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {t('version.iosAttentionLocalesWarning', { count: iosAttentionLocales.length })}
            </p>
          )}
        </ModalLayout>

        {/* Çevirileri temizle – dil seçim ekranı (Export / AI Çeviri ile aynı modal düzeni) */}
        <ModalLayout
          open={showClearLocalesModal}
          onClose={() => setShowClearLocalesModal(false)}
          title={t('version.clearLocalesModalTitle')}
          icon={<Trash2 className="h-5 w-5 text-red-500" />}
          description={t('version.clearLocalesModalDescription')}
          primaryLabel={
            clearLocalesSaving
              ? t('version.clearing')
              : clearLocalesSelected.length === 0
                ? t('version.selectLocale')
                : t('version.clearWithCount', { count: clearLocalesSelected.length })
          }
          onPrimary={handleConfirmClearLocales}
          primaryDisabled={clearLocalesSelected.length === 0 || clearLocalesSaving}
          maxWidth="md"
        >
          <TargetLocaleChecklist
            selected={clearLocalesSelected}
            onChange={setClearLocalesSelected}
            locales={clearLocalesOptions}
            disabled={clearLocalesSaving}
            listClassName="max-h-64 overflow-y-auto"
          />
        </ModalLayout>
        </>
      )}
    </AppPageLayout>
  );
};

export default VersionEditPage;
